import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Assignment as TaskIcon,
  Cloud as CloudIcon,
  Storage as StorageIcon,
  Category as CategoryIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import internalServiceService from '../../services/internal-service.service';
import ApiService from '../../services/api.service';
import { Tab, Tabs } from '@mui/material';
import { Workspaces as GroupIcon, Description as DocTypeIcon, AccountBalance as ProvidersIcon } from '@mui/icons-material';
import InternalServiceDialog from './InternalServiceDialog';
import tcDocumentTypeService from '../../services/tc-document-type.service';
import tcServiceCategoryService from '../../services/tc-service-category.service';
import serviceFieldService from '../../services/service-field.service';
import ServiceTemplateDialog from './ServiceTemplateDialog';
import ServiceGroups from './ServiceGroups';
import DocumentTypeSettings from './DocumentTypeSettings';
import ServiceCategories from './ServiceCategories';
import ServiceProviders from '../settings/ServiceProviders';
import { getBrandedTableHeadSx } from '../../utils/colorUtils';
import { ProTableStats } from '../../components/pro-table';
import { canUseConnections, can } from '../../utils/roleUtils';

// The UAE template's seed data is being corrected - the import button stays hidden until then.
const TAB_SX = { textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem', minHeight: 40 };

const UAE_TEMPLATE_ENABLED = false;

// Tab order on this page.
const TAB_SERVICES = 0;
const TAB_CATEGORIES = 1;
const TAB_GROUPS = 2;
const TAB_DOC_TYPES = 3;
const TAB_PROVIDERS = 4;
// ?tab= values for links (e.g. the old /settings/service-providers redirects here).
const TAB_BY_PARAM = { services: TAB_SERVICES, categories: TAB_CATEGORIES, groups: TAB_GROUPS, 'document-types': TAB_DOC_TYPES, providers: TAB_PROVIDERS };

const fontSize = {
  xs: '0.75rem',
  sm: '0.8125rem',
  md: '0.875rem',
};

const ServiceCatalog = ({ currentUser }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // Hide Thaklis services when the TC has Thaklis features off, OR when Thaklis services are turned off
  // in Work Order settings (thaklis_services_enabled) — hides the tile, the Type filter, and the rows.
  // `thaklisServicesOff` starts true so Thaklis stays hidden until the WO config confirms it's on.
  const [thaklisServicesOff, setThaklisServicesOff] = useState(true);
  const thaklisDisabled = currentUser?.thaklisEnabled === false || thaklisServicesOff;
  // A connections-enabled TC (promoted, or an independent TC granted the feature) never auto-creates
  // internal tasks (it delegates to B2Bs), so the "Creates Task" concept is hidden — column + toggle.
  const hideCreatesTask = canUseConnections(currentUser);

  // Data states
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  // Seed default document types on demand; bumping the key reloads the Document Types tab if open.
  const [seedingDocTypes, setSeedingDocTypes] = useState(false);
  const [docTypesRefreshKey, setDocTypesRefreshKey] = useState(0);
  const handleSeedDocTypes = async () => {
    setSeedingDocTypes(true);
    try {
      await tcDocumentTypeService.seedDefaults();
      setDocTypesRefreshKey(k => k + 1);
      setSuccess('Default document types seeded');
    } catch (err) {
      setError('Failed to seed document types');
    } finally {
      setSeedingDocTypes(false);
    }
  };

  // The TC's own service categories: feed the category filter and the add/edit service dialog.
  const [categories, setCategories] = useState([]);
  const [seedingCategories, setSeedingCategories] = useState(false);
  const [categoriesRefreshKey, setCategoriesRefreshKey] = useState(0);
  const loadCategories = async () => {
    try {
      setCategories(await tcServiceCategoryService.getAll());
    } catch (err) {
      console.error('Error loading service categories:', err);
    }
  };
  const handleSeedCategories = async () => {
    setSeedingCategories(true);
    try {
      const result = await tcServiceCategoryService.seedDefaults();
      await loadCategories();
      setCategoriesRefreshKey(k => k + 1);
      setSuccess(result?.created ? `${result.created} default categories added` : 'Default categories already present');
    } catch (err) {
      setError('Failed to seed service categories');
    } finally {
      setSeedingCategories(false);
    }
  };
  // Category added/renamed/deleted on the Service Categories tab: renames change the names shown on services.
  const handleCategoriesChanged = () => {
    loadCategories();
    loadServices();
  };

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [categoryFilter, setCategoryFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ACTIVE');

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(TAB_BY_PARAM[searchParams.get('tab')] ?? TAB_SERVICES);

  // Load services on mount
  useEffect(() => {
    loadServices();
    loadCategories();
  }, []);

  // Read the TC's Work Order settings to learn whether Thaklis services are offered.
  useEffect(() => {
    const tcId = currentUser?.typingCenterId || currentUser?.typing_center_id;
    if (!tcId) return;
    let active = true;
    (async () => {
      try {
        const resp = await ApiService.getWorkOrderConfig(tcId);
        const c = resp?.data?.data || resp?.data || {};
        if (active) setThaklisServicesOff(c.thaklis_services_enabled !== true);
      } catch (err) {
        // Leave Thaklis hidden if the config can't be read (matches the WO create page's default-off).
      }
    })();
    return () => { active = false; };
  }, [currentUser]);

  const loadServices = async () => {
    setLoading(true);
    setError(null);
    try {
      const allServices = await internalServiceService.getAllServices();
      setServices(allServices);
    } catch (err) {
      console.error('Error loading services:', err);
      setError('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  // Filter services
  const filteredServices = services.filter(service => {
    // Thaklis services are hidden entirely when Thaklis is disabled for this TC (features off, or
    // Thaklis services turned off in WO settings). getAllServices may still return them, so drop here.
    if (thaklisDisabled && service.type === 'THAKLIS') return false;

    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchesSearch =
        service.name?.toLowerCase().includes(search) ||
        service.description?.toLowerCase().includes(search) ||
        service.code?.toLowerCase().includes(search);
      if (!matchesSearch) return false;
    }

    // Type filter
    if (typeFilter !== 'ALL' && service.type !== typeFilter) return false;

    // Category filter
    if (categoryFilter !== 'ALL' && service.categoryId !== categoryFilter) return false;

    // Status filter
    if (statusFilter !== 'ALL' && service.status !== statusFilter) return false;

    return true;
  });

  // Calculate statistics
  const stats = internalServiceService.getStatistics(services);

  // Handlers
  const handleAddService = () => {
    setEditingService(null);
    setDialogOpen(true);
  };

  const handleEditService = (service) => {
    if (service.type === 'THAKLIS') {
      setError('Thaklis services cannot be edited. They are managed by the Thaklis platform.');
      return;
    }
    setEditingService(service);
    setDialogOpen(true);
  };

  const handleDeleteService = async (service) => {
    if (service.type === 'THAKLIS') {
      setError('Thaklis services cannot be deleted. They are managed by the Thaklis platform.');
      return;
    }

    if (window.confirm(`Are you sure you want to delete "${service.name}"? This will set the service as inactive.`)) {
      try {
        await internalServiceService.delete(service.id);
        setSuccess('Service deleted successfully');
        loadServices();
      } catch (err) {
        console.error('Error deleting service:', err);
        setError('Failed to delete service');
      }
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingService(null);
  };

  const handleDialogSave = async (serviceData) => {
    try {
      const docReqs = serviceData._docRequirements;
      delete serviceData._docRequirements;
      const customFields = serviceData._customFields;
      delete serviceData._customFields;

      let savedService;
      if (editingService) {
        savedService = await internalServiceService.update(editingService.id, serviceData);
        setSuccess('Service updated successfully');
      } else {
        savedService = await internalServiceService.create(serviceData);
        setSuccess('Service created successfully');
      }

      // Save document requirements if any
      const serviceId = savedService?.id || editingService?.id;
      if (serviceId && docReqs && docReqs.length >= 0) {
        try {
          await tcDocumentTypeService.saveRequirements(serviceId, docReqs);
        } catch (err) {
          console.error('Error saving doc requirements:', err);
        }
      }
      // Save custom fields if any
      if (serviceId && customFields) {
        try {
          await serviceFieldService.saveFields(serviceId, customFields);
        } catch (err) {
          console.error('Error saving custom fields:', err);
        }
      }
      loadServices();
      handleDialogClose();
    } catch (err) {
      console.error('Error saving service:', err);
      setError('Failed to save service');
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getTypeChip = (type) => {
    if (type === 'THAKLIS') {
      return (
        <Chip
          icon={<CloudIcon sx={{ fontSize: 14 }} />}
          label="Thaklis"
          size="small"
          color="primary"
          variant="outlined"
          sx={{ fontSize: fontSize.xs }}
        />
      );
    }
    return (
      <Chip
        icon={<StorageIcon sx={{ fontSize: 14 }} />}
        label="Internal"
        size="small"
        color="success"
        variant="outlined"
        sx={{ fontSize: fontSize.xs }}
      />
    );
  };

  const getStatusChip = (status) => {
    return (
      <Chip
        label={status}
        size="small"
        color={status === 'ACTIVE' ? 'success' : 'default'}
        sx={{ fontSize: fontSize.xs }}
      />
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 0 }}>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto"
        sx={{ mb: 2, minHeight: 40, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Services" sx={TAB_SX} />
        <Tab label="Service Categories" icon={<CategoryIcon sx={{ fontSize: 16 }} />} iconPosition="start" sx={TAB_SX} />
        <Tab label="Service Groups" icon={<GroupIcon sx={{ fontSize: 16 }} />} iconPosition="start" sx={TAB_SX} />
        <Tab label="Document Types" icon={<DocTypeIcon sx={{ fontSize: 16 }} />} iconPosition="start" sx={TAB_SX} />
        {can(currentUser, 'settings', 'read') && (
          <Tab value={TAB_PROVIDERS} label="Service Providers" icon={<ProvidersIcon sx={{ fontSize: 16 }} />} iconPosition="start" sx={TAB_SX} />
        )}
      </Tabs>

      {activeTab === TAB_CATEGORIES && (
        <ServiceCategories refreshKey={categoriesRefreshKey} onChanged={handleCategoriesChanged}
          onSeed={handleSeedCategories} seeding={seedingCategories} canWrite={can(currentUser, 'service_catalog', 'write')} />
      )}
      {activeTab === TAB_GROUPS && <ServiceGroups />}
      {activeTab === TAB_PROVIDERS && <ServiceProviders currentUser={currentUser} />}
      {activeTab === TAB_DOC_TYPES && (
        <DocumentTypeSettings refreshKey={docTypesRefreshKey}
          onSeed={handleSeedDocTypes} seeding={seedingDocTypes} canWrite={can(currentUser, 'service_catalog', 'write')} />
      )}

      {activeTab === TAB_SERVICES && <>
      <ProTableStats
        cards={[
          { key: 'ALL', label: 'Total Services', value: stats.total },
          ...(!thaklisDisabled ? [{ key: 'THAKLIS', label: 'Thaklis Services', value: stats.thaklis, color: 'info.main' }] : []),
          { key: 'INTERNAL', label: 'Internal Services', value: stats.internal, color: 'success.main' },
        ].map((c) => ({ ...c, highlight: typeFilter === c.key, onClick: () => setTypeFilter(c.key) }))
          .concat([{ key: 'TASKS', label: 'With Tasks', value: stats.withTasks, color: 'warning.main' }])}
      />

      {/* Filters — inline flex matching ProTable style */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1.5 }}>
            <TextField
              size="small"
              placeholder="Search services..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                  </InputAdornment>
                )
              }}
              sx={{ flex: '1 1 200px', minWidth: 200, '& .MuiInputBase-root': { height: 32 }, '& .MuiInputBase-input': { fontSize: '0.8125rem' } }}
            />
          {!thaklisDisabled && (
            <FormControl size="small" sx={{ minWidth: 120, '& .MuiInputBase-root': { height: 32 }, '& .MuiSelect-select': { fontSize: '0.8125rem' } }}>
              <InputLabel sx={{ fontSize: '0.8125rem' }}>Type</InputLabel>
              <Select value={typeFilter} label="Type" onChange={(e) => setTypeFilter(e.target.value)}>
                <MenuItem value="ALL">All</MenuItem>
                <MenuItem value="THAKLIS">Thaklis</MenuItem>
                <MenuItem value="INTERNAL">Internal</MenuItem>
              </Select>
            </FormControl>
          )}
            <FormControl size="small" sx={{ minWidth: 140, '& .MuiInputBase-root': { height: 32 }, '& .MuiSelect-select': { fontSize: '0.8125rem' } }}>
              <InputLabel sx={{ fontSize: '0.8125rem' }}>Category</InputLabel>
              <Select value={categoryFilter} label="Category" onChange={(e) => setCategoryFilter(e.target.value)}>
                <MenuItem value="ALL">All</MenuItem>
                {categories.map(cat => (
                  <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 110, '& .MuiInputBase-root': { height: 32 }, '& .MuiSelect-select': { fontSize: '0.8125rem' } }}>
              <InputLabel sx={{ fontSize: '0.8125rem' }}>Status</InputLabel>
              <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
                <MenuItem value="ALL">All</MenuItem>
                <MenuItem value="ACTIVE">Active</MenuItem>
                <MenuItem value="INACTIVE">Inactive</MenuItem>
              </Select>
            </FormControl>
            <Button size="small" onClick={() => { setSearchTerm(''); setTypeFilter('ALL'); setCategoryFilter('ALL'); setStatusFilter('ACTIVE'); }}
              sx={{ fontSize: '0.75rem', textTransform: 'none', color: 'text.secondary' }}>
              Reset
            </Button>
        <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto', flexShrink: 0, alignItems: 'center' }}>
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', whiteSpace: 'nowrap', mr: 1 }}>
            {filteredServices.length} services
          </Typography>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={loadServices}
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {can(currentUser, 'service_catalog', 'write') && (
            <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={handleAddService}
              sx={{ textTransform: 'none', fontSize: '0.8125rem', ml: 0.5 }}>
              Add Service
            </Button>
          )}
        </Box>
      </Box>

      {/* Services Table */}
      <Paper sx={{ overflow: 'hidden', borderRadius: 2 }}>
        <TableContainer>
          <Table size="small">
            <TableHead sx={getBrandedTableHeadSx(theme)}>
              <TableRow sx={{ '& th': { fontSize: '0.75rem', fontWeight: 600, py: 0.75 } }}>
                <TableCell>Service</TableCell>
                {!isSmallMobile && <TableCell>Category</TableCell>}
                {!isMobile && <TableCell align="right">Govt Fee</TableCell>}
                <TableCell align="right">Service Fee</TableCell>
                {!isMobile && <TableCell align="right">Tax</TableCell>}
                <TableCell align="center">Type</TableCell>
                <TableCell align="center">Mapped</TableCell>
                {!isSmallMobile && !hideCreatesTask && <TableCell align="center" sx={{ width: 50 }}>Task</TableCell>}
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredServices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isMobile ? 6 : (hideCreatesTask ? 9 : 10)} align="center">
                    <Box py={4}>
                      <CategoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                      <Typography color="text.secondary">
                        No services found
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                filteredServices
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((service) => (
                    <TableRow key={service.id} hover>
                      <TableCell>
                        <Box>
                          <Typography sx={{ fontSize: fontSize.sm }}>
                            {service.name}
                          </Typography>
                          {service.description && !isSmallMobile && (
                            <Typography sx={{ fontSize: fontSize.xs }}>
                              {service.description.length > 60
                                ? `${service.description.substring(0, 60)}...`
                                : service.description}
                            </Typography>
                          )}
                          {isSmallMobile && (
                            <Typography sx={{ fontSize: fontSize.xs }}>
                              {internalServiceService.getCategoryLabel(service.category)}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      {!isSmallMobile && (
                        <TableCell>
                          <Typography sx={{ fontSize: fontSize.sm }}>
                            {internalServiceService.getCategoryLabel(service.category)}
                          </Typography>
                        </TableCell>
                      )}
                      {!isMobile && (
                        <TableCell align="right">
                          <Typography sx={{ fontSize: fontSize.sm }}>
                            {service.govtCharge > 0
                              ? internalServiceService.formatCurrency(service.govtCharge)
                              : '-'}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell align="right">
                        <Typography sx={{ fontSize: fontSize.sm }}>
                          {internalServiceService.formatCurrency(service.serviceCharge)}
                        </Typography>
                      </TableCell>
                      {!isMobile && (
                        <TableCell align="right">
                          <Typography sx={{ fontSize: fontSize.sm }}>
                            {service.taxOnServiceCharge > 0
                              ? internalServiceService.formatCurrency(service.taxOnServiceCharge)
                              : '-'}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell align="center">
                        {getTypeChip(service.type)}
                      </TableCell>
                      <TableCell align="center">
                        {service.type === 'INTERNAL' ? (
                          <Chip
                            size="small"
                            label={service.uniformPricing !== false ? 'Yes' : 'No'}
                            color={service.uniformPricing !== false ? 'success' : 'default'}
                            sx={{ fontSize: '0.7rem' }}
                          />
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                      {!isSmallMobile && !hideCreatesTask && (
                        <TableCell align="center">
                          {service.type === 'INTERNAL' && service.createsTask ? (
                            <Tooltip title="Creates internal task when added to work order">
                              <Chip
                                icon={<TaskIcon sx={{ fontSize: 14 }} />}
                                label="Yes"
                                size="small"
                                color="info"
                                variant="outlined"
                                sx={{ fontSize: fontSize.xs }}
                              />
                            </Tooltip>
                          ) : service.type === 'THAKLIS' ? (
                            <Tooltip title="Creates Thaklis ticket">
                              <Chip
                                label="Ticket"
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ fontSize: fontSize.xs }}
                              />
                            </Tooltip>
                          ) : (
                            <Typography sx={{ fontSize: fontSize.xs }}>-</Typography>
                          )}
                        </TableCell>
                      )}
                      <TableCell align="center">
                        {getStatusChip(service.status)}
                      </TableCell>
                      <TableCell align="center">
                        {service.type === 'INTERNAL' ? (
                          <Box display="flex" justifyContent="center" gap={0.5}>
                            {can(currentUser, 'service_catalog', 'write') && (
                              <Tooltip title="Edit service">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditService(service)}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {can(currentUser, 'service_catalog', 'delete') && (
                              <Tooltip title="Delete service">
                                <IconButton
                                  size="small"
                                  sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}
                                  onClick={() => handleDeleteService(service)}
                                >
                                  <DeleteIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        ) : (
                          <Typography sx={{ fontSize: fontSize.xs }}>
                            View Only
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredServices.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      {/* Internal Service Dialog */}
      <InternalServiceDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onSave={handleDialogSave}
        service={editingService}
        categories={categories}
        hideCreatesTask={hideCreatesTask}
      />

      </>}

      {/* UAE Template Import Dialog */}
      <ServiceTemplateDialog
        open={templateDialogOpen}
        onClose={() => setTemplateDialogOpen(false)}
        onImportComplete={(result) => {
          setSuccess(`Imported ${result.created} services, ${result.aliasesCreated} AI aliases, ${result.groupsCreated} groups (${result.skipped} skipped)`);
          loadServices();
          loadCategories(); // the import seeds default categories when the TC has none
        }}
      />
    </Box>
  );
};

export default ServiceCatalog;
